import * as Slider from '@radix-ui/react-slider'
import { NumberInput } from 'components/form/inputs/NumberInput'
import { useKestrelViewerContext } from 'features/kestrelViewer'
import { KestrelCommands } from 'features/kestrelViewer/types/KestrelCommands'
import React, { useEffect } from 'react'
import styled, { keyframes } from 'styled-components'
import { KVPosition, KVRotation } from 'types'

const fadeInMini = keyframes`
  from {
    opacity: 0;
    transform: translateY(0.625rem);
  }
  to {
    opacity: 0.95;
    transform: translateY(0);
  }
`

const ControlsContainer = styled.div`
  position: absolute;
  top: 3.75rem;
  right: 1.25rem;
  background: rgba(0, 0, 0, 0.6);
  padding: 0.5rem;
  padding: 0.5rem;
  border-radius: 0.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  min-width: 14rem;
  width: 16rem;
  min-width: 14rem;
  width: 16rem;
  animation: ${fadeInMini} 0.3s ease-out forwards;
  opacity: 0.95;
  transition: opacity 0.2s ease;
  backdrop-filter: blur(4px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  cursor: default;
`

const ControlGroup = styled.div`
  margin-bottom: 0.25rem;
  margin-bottom: 0.25rem;
`

const ControlLabel = styled.div`
  font-size: 0.875rem;
  opacity: 0.95;
  letter-spacing: 0.05rem;
  margin-bottom: 0.125rem;
`

const SliderRow = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 0.125rem;
`

const SliderLabel = styled.div`
  display: flex;
  align-items: center;
  font-size: 0.75rem;
  width: 4rem;
`

const SliderContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

const StyledSliderRoot = styled(Slider.Root)`
  position: relative;
  display: flex;
  align-items: center;
  user-select: none;
  touch-action: none;
  height: 20px;
`

const StyledSliderTrack = styled(Slider.Track)`
  background-color: ${({ theme }) => theme.forecast.background3};
  position: relative;
  border-radius: 9999px;
  height: 4px;
  width: 7rem;
`

const StyledSliderRange = styled(Slider.Range)`
  position: absolute;
  background-color: ${({ theme }) => theme.forecast.primary};
  border-radius: 9999px;
  height: 100%;
`

const StyledSliderThumb = styled(Slider.Thumb)`
  display: block;
  width: 8px;
  height: 20px;
  background-color: ${({ theme }) => theme.forecast.activeText};
  border-radius: 9999px;
`

const StyledNumberInput = styled(NumberInput)`
  width: 4rem;
  border-radius: 0.25rem;
  border: 1px solid ${({ theme }) => theme.forecast.border2};
  background-color: ${({ theme }) => theme.forecast.background3};
`

const StyledButton = styled.button`
  background: ${({ theme }) => theme.forecast.defaultButton};
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 0.25rem;
  font-size: 0.875rem;
  cursor: pointer;
  width: 100%;
  margin-top: 0.5rem;
  transition: opacity 0.2s ease;

  &:hover {
    background: ${({ theme }) => theme.forecast.hoverButton};
  }

  &:active {
    background: ${({ theme }) => theme.forecast.activeButton};
  }
`

export const PlaneControls: React.FC = () => {
  const {
    sendCommand,
    planePosition,
    setPlanePosition,
    planeRotation,
    setPlaneRotation,
  } = useKestrelViewerContext()

  const positionValues = planePosition ?? new KVPosition(0, 0, 0)
  const rotationValues = planeRotation ?? new KVRotation(0, 0, 0)

  useEffect(() => {
    if (!planePosition) {
      const initialPosition = new KVPosition(0, 0, 0)
      setPlanePosition?.(initialPosition)

      const positionAxes = ['x', 'y', 'z'] as const
      positionAxes.forEach((axis) => {
        sendCommand({
          command: KestrelCommands.SET_2D_PLANE_POSITION,
          axis,
          value: 0,
        })
      })
    }

    if (!planeRotation) {
      const initialRotation = new KVRotation(0, 0, 0)
      setPlaneRotation?.(initialRotation)

      const rotationAxes = ['pitch', 'yaw', 'roll'] as const
      rotationAxes.forEach((axis) => {
        sendCommand({
          command: KestrelCommands.SET_2D_PLANE_ROTATION,
          axis,
          value: 0,
        })
      })
    }
  }, [
    sendCommand,
    setPlanePosition,
    setPlaneRotation,
    planePosition,
    planeRotation,
  ])

  const handlePositionChange = (axis: 'x' | 'y' | 'z', value: number) => {
    if (value > 1) value = 1
    if (value < -1) value = -1

    const newPosition = { ...positionValues, [axis]: value }
    setPlanePosition?.(newPosition)

    sendCommand({
      command: KestrelCommands.SET_2D_PLANE_POSITION,
      axis,
      value,
    })
  }

  const handleRotationChange = (
    axis: 'pitch' | 'yaw' | 'roll',
    value: number
  ) => {
    if (value > 180) value = 180
    if (value < -180) value = -180

    const newRotation = { ...rotationValues, [axis]: value }
    setPlaneRotation?.(newRotation)

    sendCommand({
      command: KestrelCommands.SET_2D_PLANE_ROTATION,
      axis,
      value,
    })
  }

  const handleSliceOnOrbit = () => {
    sendCommand({
      command: KestrelCommands.SLICE_ON_ORBIT,
    })
  }

  const handleGetPlaneIntersection = () => {
    sendCommand({
      command: KestrelCommands.GET_PLANE_INTERSECTION,
    })
  }

  return (
    <ControlsContainer>
      <ControlGroup>
        <ControlLabel>Position</ControlLabel>
        {(['x', 'y', 'z'] as const).map((axis) => (
          <SliderRow key={axis}>
            <SliderLabel>{axis.toUpperCase()}</SliderLabel>
            <SliderContainer>
              <StyledSliderRoot
                min={-1}
                max={1}
                step={0.01}
                value={[positionValues[axis]]}
                onValueChange={([value]) => handlePositionChange(axis, value)}>
                <StyledSliderTrack>
                  <StyledSliderRange />
                </StyledSliderTrack>
                <StyledSliderThumb />
              </StyledSliderRoot>
              <StyledNumberInput
                type='number'
                min={-1}
                max={1}
                step={0.01}
                value={positionValues[axis]}
                onChange={(e) =>
                  handlePositionChange(axis, parseFloat(e.target.value))
                }
              />
            </SliderContainer>
          </SliderRow>
        ))}
      </ControlGroup>

      <ControlGroup>
        <ControlLabel>Rotation</ControlLabel>
        {(['pitch', 'yaw', 'roll'] as const).map((axis) => (
          <SliderRow key={axis}>
            <SliderLabel>
              {axis.charAt(0).toUpperCase() + axis.slice(1)}
            </SliderLabel>
            <SliderContainer>
              <StyledSliderRoot
                min={-180}
                max={180}
                step={1}
                value={[rotationValues[axis]]}
                onValueChange={([value]) => handleRotationChange(axis, value)}>
                <StyledSliderTrack>
                  <StyledSliderRange />
                </StyledSliderTrack>
                <StyledSliderThumb />
              </StyledSliderRoot>
              <StyledNumberInput
                type='number'
                min={-180}
                max={180}
                step={1}
                value={rotationValues[axis]}
                onChange={(e) =>
                  handleRotationChange(axis, parseFloat(e.target.value))
                }
              />
            </SliderContainer>
          </SliderRow>
        ))}
      </ControlGroup>
      <StyledButton onClick={handleSliceOnOrbit}>Slice on Orbit</StyledButton>
      <StyledButton onClick={handleGetPlaneIntersection}>
        Get Plane Intersection
      </StyledButton>
    </ControlsContainer>
  )
}
